/**
 * AbortScheduleDialog — confirm modal for STOPPING a running schedule.
 *
 * Stopping a schedule is not a pause: it (1) breaks the event-driven re-fire
 * loop so no further runs start, and (2) **aborts the run in progress** via
 * ``StopExecution`` on the runtime state machine. Because that kills live work,
 * the user must confirm through this dialog (matches the house modal pattern,
 * e.g. ``DeleteConnectionDialog``) rather than a bare ``window.confirm``.
 *
 * Presenter only — the caller owns the ``onConfirm`` (the toggle-to-disable
 * call) and closes on success.
 */
import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { StopCircle } from 'lucide-react';

export default function AbortScheduleDialog({ open, scheduleName, onConfirm, onClose }) {
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setSubmitting(false);
    setSubmitError(null);
  }, [open, scheduleName]);

  const handleConfirm = async () => {
    if (submitting) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      await onConfirm?.();
      // Caller closes + refreshes on success.
    } catch (err) {
      setSubmitError(err?.message || 'Abort failed');
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          aria-modal="true"
          role="dialog"
        >
          <motion.div
            className="w-full max-w-md bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden"
            initial={{ scale: 0.98, y: 6 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.98, y: 6 }}
          >
            <header className="px-5 py-4 border-b border-gray-100 flex items-start gap-3">
              <StopCircle className="h-5 w-5 shrink-0 text-red-600 mt-0.5" aria-hidden />
              <div className="min-w-0">
                <h3 className="text-base font-semibold text-gray-900">Abort schedule</h3>
                <p className="text-xs text-gray-600 mt-1 truncate">
                  <span className="font-medium text-gray-700">{scheduleName || 'this schedule'}</span>
                </p>
              </div>
            </header>

            <div className="px-5 py-4 space-y-3">
              <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900">
                <p className="font-medium">This stops the schedule now.</p>
                <ul className="mt-1 list-disc pl-4 space-y-0.5 text-[13px]">
                  <li>The run in progress is <strong>aborted</strong> (its agents stop mid-work).</li>
                  <li>No further runs will start — the loop is broken.</li>
                </ul>
                <p className="mt-1.5 text-[13px]">
                  Partial output already written to the workspace is kept. You can
                  start the schedule again later.
                </p>
              </div>

              {submitError && (
                <p className="text-sm text-red-700 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                  {submitError}
                </p>
              )}

              <div className="flex items-center justify-end gap-2 pt-1">
                <button
                  type="button"
                  onClick={onClose}
                  className="text-sm px-3 py-1.5 rounded-lg border border-gray-300 text-gray-800 hover:bg-gray-50"
                  disabled={submitting}
                >
                  Keep running
                </button>
                <button
                  type="button"
                  onClick={handleConfirm}
                  disabled={submitting}
                  className="text-sm px-3 py-1.5 rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
                  aria-label="Abort schedule and stop the running job"
                  data-testid="abort-schedule-confirm"
                >
                  <StopCircle className="h-4 w-4" aria-hidden />
                  {submitting ? 'Aborting…' : 'Abort & stop'}
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
